import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@/hooks/useApiClient';
import { Link } from 'react-router-dom';

export default function AdminDashboardPage() {
  const { t } = useTranslation();
  const apiClient = useApiClient();

  const subscriptionsQuery = useQuery({
    queryKey: ['admin:subscriptions-overview'],
    queryFn: () => apiClient.getAdminSubscriptions(0, 100),
  });

  const establishmentsQuery = useQuery({
    queryKey: ['admin:establishments-overview'],
    queryFn: () => apiClient.getAdminEstablishments(0, 100),
  });

  const billingQuery = useQuery({
    queryKey: ['admin:billing-overview'],
    queryFn: () => apiClient.getBillingDashboard(),
  });

  const supportQuery = useQuery({
    queryKey: ['admin:support-overview'],
    queryFn: () => apiClient.getAdminSupportTickets(0, 100),
  });

  const totalSubscriptions = subscriptionsQuery.data?.total || 0;
  const activeSubscriptions = subscriptionsQuery.data?.data?.filter((s: any) => s.status === 'ACTIVE').length || 0;
  const pastDueSubscriptions = subscriptionsQuery.data?.data?.filter((s: any) => s.status === 'PAST_DUE').length || 0;
  const trialSubscriptions = subscriptionsQuery.data?.data?.filter((s: any) => s.status === 'TRIALING').length || 0;

  const totalEstablishments = establishmentsQuery.data?.total || 0;
  const totalUsers = billingQuery.data?.totalUsers || 0;
  const totalRevenue = billingQuery.data?.totalRevenue || 0;
  const mrr = billingQuery.data?.monthlyRecurringRevenue || 0;

  const openTickets = supportQuery.data?.data?.filter((t: any) => t.status === 'OPEN').length || 0;
  const inProgressTickets = supportQuery.data?.data?.filter((t: any) => t.status === 'IN_PROGRESS').length || 0;

  const isLoading = subscriptionsQuery.isLoading || establishmentsQuery.isLoading || billingQuery.isLoading;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900">👑 {t('admin.dashboard')}</h1>
        <p className="text-neutral-600 mt-2">{t('admin.dashboardSubtitle')}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">{t('admin.totalRevenue')}</p>
              <p className="text-3xl font-bold mt-2">
                {isLoading ? '...' : `${(totalRevenue / 100).toFixed(2)} €`}
              </p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <span className="text-2xl">💰</span>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-white/20">
            <p className="text-green-100 text-sm">
              MRR: <span className="text-white font-semibold">{(mrr / 100).toFixed(2)} €</span>
            </p>
          </div>
        </div>

        <Link
          to="/admin/subscriptions"
          className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg hover:shadow-xl transition-all hover:scale-[1.02]"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">{t('admin.subscriptions')}</p>
              <p className="text-3xl font-bold mt-2">{isLoading ? '...' : totalSubscriptions}</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <span className="text-2xl">💳</span>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-white/20 flex gap-4 text-sm">
            <span className="text-green-200">✓ {activeSubscriptions} {t('admin.active')}</span>
            <span className="text-amber-200">⚠ {pastDueSubscriptions} {t('admin.pastDue')}</span>
          </div>
        </Link>

        <Link
          to="/admin/establishments"
          className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg hover:shadow-xl transition-all hover:scale-[1.02]"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">{t('admin.establishments')}</p>
              <p className="text-3xl font-bold mt-2">{isLoading ? '...' : totalEstablishments}</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <span className="text-2xl">🏢</span>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-white/20">
            <p className="text-purple-100 text-sm">
              {t('admin.totalUsers')}: <span className="text-white font-semibold">{totalUsers}</span>
            </p>
          </div>
        </Link>

        <Link
          to="/admin/support"
          className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg hover:shadow-xl transition-all hover:scale-[1.02]"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm font-medium">{t('admin.support')}</p>
              <p className="text-3xl font-bold mt-2">{isLoading ? '...' : (openTickets + inProgressTickets)}</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <span className="text-2xl">🎟️</span>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-white/20 flex gap-4 text-sm">
            <span className="text-red-200">🔴 {openTickets} {t('admin.open')}</span>
            <span className="text-yellow-200">🟡 {inProgressTickets} {t('admin.inProgress')}</span>
          </div>
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-neutral-200 shadow-sm">
          <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
            <h2 className="font-bold text-neutral-900">💳 {t('admin.subscriptionBreakdown')}</h2>
            <Link to="/admin/subscriptions" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
              {t('admin.seeAll')}
            </Link>
          </div>
          <div className="p-6">
            {isLoading ? (
              <div className="text-center py-4 sm:py-8 text-neutral-500">{t('common.loading')}</div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span className="text-neutral-700">{t('admin.active')}</span>
                  </div>
                  <span className="font-bold text-neutral-900">{activeSubscriptions}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <span className="text-neutral-700">{t('admin.trial')}</span>
                  </div>
                  <span className="font-bold text-neutral-900">{trialSubscriptions}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                    <span className="text-neutral-700">{t('admin.pastDue')}</span>
                  </div>
                  <span className="font-bold text-neutral-900">{pastDueSubscriptions}</span>
                </div>
                <div className="flex items-center justify-between border-t border-neutral-200 pt-4 mt-4">
                  <span className="text-neutral-700 font-medium">{t('admin.total')}</span>
                  <span className="font-bold text-neutral-900">{totalSubscriptions}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-neutral-200 shadow-sm">
          <div className="px-6 py-4 border-b border-neutral-200">
            <h2 className="font-bold text-neutral-900">⚡ {t('admin.quickAccess')}</h2>
          </div>
          <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Link to="/admin/subscriptions" className="p-4 rounded-lg border border-neutral-200 hover:border-blue-500 hover:bg-blue-50 transition-all group">
              <span className="text-2xl">💳</span>
              <p className="font-medium text-neutral-900 mt-2 group-hover:text-blue-600">{t('admin.subscriptions')}</p>
              <p className="text-sm text-neutral-500">{t('admin.manageSubscriptions')}</p>
            </Link>
            <Link to="/admin/establishments" className="p-4 rounded-lg border border-neutral-200 hover:border-purple-500 hover:bg-purple-50 transition-all group">
              <span className="text-2xl">🏢</span>
              <p className="font-medium text-neutral-900 mt-2 group-hover:text-purple-600">{t('admin.establishments')}</p>
              <p className="text-sm text-neutral-500">{t('admin.establishmentsOverview')}</p>
            </Link>
            <Link to="/admin/audit-logs" className="p-4 rounded-lg border border-neutral-200 hover:border-slate-500 hover:bg-slate-50 transition-all group">
              <span className="text-2xl">📋</span>
              <p className="font-medium text-neutral-900 mt-2 group-hover:text-slate-600">{t('admin.auditLogs')}</p>
              <p className="text-sm text-neutral-500">{t('admin.auditHistory')}</p>
            </Link>
            <Link to="/admin/billing" className="p-4 rounded-lg border border-neutral-200 hover:border-green-500 hover:bg-green-50 transition-all group">
              <span className="text-2xl">📊</span>
              <p className="font-medium text-neutral-900 mt-2 group-hover:text-green-600">{t('admin.billing')}</p>
              <p className="text-sm text-neutral-500">{t('admin.revenueAndStats')}</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
